// src/components/admin/ValidateOtherAttributesBoardAdmin.tsx
import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Card,
  Code,
  Divider,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Switch,
} from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useGetProjectByProjectIdIssuesValidateOtherAttributesByBoardQuery } from "../../../../openapi";

type Row = {
  issueId: number;
  key: string;
  typeInConf: string | null | undefined;
  value: unknown;
  isTypeValid: boolean;
};

const isMongoId24 = (s: string) => s.trim().length === 24;

export const ValidateOtherAttributesBoardAdmin: React.FC = () => {
  const [projectId, setProjectId] = useState("");
  const [boardId, setBoardId] = useState("");
  const [onlyInvalid, setOnlyInvalid] = useState(true);

  const p = projectId.trim();
  const b = boardId.trim();

  const shouldFetch = isMongoId24(p) && isMongoId24(b);

  const queryArgs = shouldFetch ? { projectId: p, issueBoardId: b } : skipToken;

  const { data, isFetching, isError, error } =
    useGetProjectByProjectIdIssuesValidateOtherAttributesByBoardQuery(queryArgs);

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];

    for (const issue of data ?? []) {
      for (const [key, entry] of Object.entries(issue.data ?? {})) {
        out.push({
          issueId: issue.id,
          key,
          typeInConf: (entry as any).typeInConf,
          value: (entry as any).value,
          isTypeValid: Boolean((entry as any).isTypeValid),
        });
      }
    }

    return onlyInvalid ? out.filter((r) => !r.isTypeValid) : out;
  }, [data, onlyInvalid]);

  const invalidCount = useMemo(() => rows.filter((r) => !r.isTypeValid).length, [rows]);

  return (
    <Box maw={1100} mx="auto" mt="xl" px="md">
      <Card withBorder radius="lg" padding="md">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap={2}>
              <Title order={2}>Validate otherAttributesData</Title>
              <Text c="dimmed" size="sm">
                Validerer otherAttributesData mot board-konfigurasjonen (kun taskOrIssue = ISSUE). Henter automatisk når
                både ProjectId og BoardId er 24 tegn.
              </Text>
            </Stack>

            <Group gap="xs" wrap="wrap">
              <Switch
                checked={onlyInvalid}
                onChange={(e) => setOnlyInvalid(e.currentTarget.checked)}
                label="Vis kun feil"
              />
            </Group>
          </Group>

          <Divider />

          <Group align="flex-end" wrap="wrap">
            <TextInput
              label="ProjectId"
              placeholder="24 tegn (MongoId)"
              value={projectId}
              onChange={(e) => setProjectId(e.currentTarget.value)}
              w={420}
              rightSection={
                p.length === 0 ? null : isMongoId24(p) ? <IconCheck size={16} /> : <Text c="dimmed">24</Text>
              }
            />
            <TextInput
              label="BoardId"
              placeholder="24 tegn (MongoId)"
              value={boardId}
              onChange={(e) => setBoardId(e.currentTarget.value)}
              w={420}
              rightSection={
                b.length === 0 ? null : isMongoId24(b) ? <IconCheck size={16} /> : <Text c="dimmed">24</Text>
              }
            />
          </Group>

          {!shouldFetch && (p.length > 0 || b.length > 0) && (
            <Alert color="gray" title="Venter på gyldige id-er">
              Begge feltene må være nøyaktig <b>24 tegn</b> før validering kjøres automatisk.
            </Alert>
          )}

          {isError && (
            <Alert color="red" title="Feil ved validering" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">
                {(error as any)?.data?.message ?? (error as any)?.error ?? (error as any)?.message ?? "Ukjent feil"}
              </Text>
            </Alert>
          )}

          {shouldFetch && !isError && (
            <Group justify="space-between" wrap="wrap">
              <Group gap="xs">
                {isFetching ? <Loader size="sm" /> : <IconCheck size={16} />}
                <Text size="sm">{data?.length ?? 0} issues sjekket</Text>
              </Group>

              <Group gap="xs">
                <Badge color={invalidCount > 0 ? "red" : "green"} variant="filled">
                  {invalidCount} feil
                </Badge>
                <Badge variant="light">{rows.length} rader vist</Badge>
              </Group>
            </Group>
          )}

          <ScrollArea h={520} type="auto" offsetScrollbars>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 90 }}>Issue</Table.Th>
                  <Table.Th style={{ width: 220 }}>Key</Table.Th>
                  <Table.Th style={{ width: 140 }}>Type i config</Table.Th>
                  <Table.Th style={{ width: 110 }}>Valid</Table.Th>
                  <Table.Th>Value</Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" size="sm">
                        Ingen rader å vise. {onlyInvalid ? "Alt ser OK ut, eller ingen data finnes." : ""}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  rows.map((r, idx) => (
                    <Table.Tr key={`${r.issueId}-${r.key}-${idx}`}>
                      <Table.Td>
                        <Badge variant="light">{r.issueId}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Code>{r.key}</Code>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="outline">{r.typeInConf ?? "?"}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={r.isTypeValid ? "green" : "red"} variant="filled">
                          {r.isTypeValid ? "OK" : "FEIL"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Code block style={{ whiteSpace: "pre-wrap" }}>
                          {safeStringify(r.value)}
                        </Code>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Stack>
      </Card>
    </Box>
  );
};

function safeStringify(v: unknown): string {
  if (v === undefined) return "undefined";
  if (v === null) return "null";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
